import * as Sentry from "@sentry/react";
import { isArray, merge, mergeWith } from "lodash";
import { InfiniteData, QueryKey } from "react-query";
import { Timeslot } from "types";
import { queryClient } from "../api/queryClient";
import { invalidate } from "../hooks/useInvalidation";

interface Resource {
    id: ID;
}

/**
 * Update a resource in the cache.
 * If the resource does not exist in the cache, it will be created.
 * If the resource exists, the previous data will be merged with new data (for cases where the cached resource is expanded when fetched from the API).
 */
export const updateCache = <T extends Resource | Timeslot>(
    collection: string,
    data: T | T[],
    shouldInvalidate = true,
    forceReplaceId?: ID,
    shouldInsertToCache = false,
    queryKey?: QueryKey,
) => {
    interface TData {
        data: T[];
    }

    try {
        // Handle cases where data is an array of resources
        if (Array.isArray(data)) {
            for (const item of data) {
                updateCache(collection, item, shouldInvalidate);
            }

            return;
        }

        // Update the resource in the cache by ID
        queryClient.setQueryData<T>([collection, data.id], (currentData) =>
            mergeWith({}, currentData, data, (objectValue, sourceValue) => {
                // If it's an array, sourceValue is used directly, skipping the recursive merge
                if (Array.isArray(objectValue)) {
                    return sourceValue;
                }
            }),
        );

        const key = queryKey ?? [collection, "collection"];

        // Find all caches for the resource collection, both arrays (collections) and pages (infinite collections)
        const queriesData = queryClient.getQueriesData<T[] | InfiniteData<T[]>>(key);

        for (const [queryKey, currentData] of queriesData) {
            if (Array.isArray(currentData)) {
                // Update resource in the collection
                queryClient.setQueryData(queryKey, updateArray(currentData, data));
            } else if (currentData) {
                // Update resource in the infinite collection

                const pages = currentData.pages?.map((pageData) => {
                    if ("data" in pageData) {
                        const { data: timeslotsPageData } = pageData as unknown as TData;

                        if (isArray(timeslotsPageData) && timeslotsPageData.length > 0) {
                            return {
                                ...pageData,
                                data: updateArray(timeslotsPageData, data, forceReplaceId, shouldInsertToCache),
                            };
                        }
                    }

                    if (pageData.length > 0) {
                        return updateArray(pageData, data);
                    }

                    return pageData;
                });

                queryClient.setQueryData(queryKey, { ...currentData, pages });
            }
        }

        // We still need to invalidate the queries
        // Cache updates are only for more responsive UI,
        // but query invalidation will ensure we always have the fresh and correct data
        if (shouldInvalidate) {
            invalidate(queryClient, collection, data.id);
        }
    } catch (error: any) {
        console.log(`Error updating cache of collection: ${collection}`, error);
        Sentry.captureException(error, { tags: { message: `Error updating cache of collection: ${collection}` } });
    }
};

/**
 * Update an item in the array using the `merge` method.
 */

const updateArray = <T extends Resource>(array: T[], data: T, forceReplaceId?: ID, shouldInsertToCache = false) => {
    if (shouldInsertToCache) {
        const itemIndex = array.findIndex((item) => item.id === data.id || item.id === forceReplaceId);

        if (itemIndex >= 0) {
            return array.map((item, index) => (index === itemIndex ? merge({}, item, data) : item));
        }

        return [...array, data];
    }

    return array.map((item) => (item.id === data.id || item.id === forceReplaceId ? merge({}, item, data) : item));
};

/**
 * This function will remove the resource from its direct query cache (if any),
 * and also from any collections (or infinite queries) where it appears.
 */
export const removeCacheItem = <T extends Resource>(collection: string, id: ID, queryKey?: QueryKey) => {
    interface TData {
        data: T[];
    }
    try {
        // Use the provided queryKey, or default to [collection, "collection"]
        const key = queryKey ?? [collection, "collection"];

        // Get all queries for this collection key
        const queriesData = queryClient.getQueriesData<T[] | InfiniteData<T[]>>(key);

        for (const [queryKey, currentData] of queriesData) {
            if (Array.isArray(currentData)) {
                // If the data is an array, simply filter out the item
                queryClient.setQueryData(
                    queryKey,
                    currentData.filter((item) => item.id !== id),
                );
            } else if (currentData) {
                // If the data represents an infinite query, update each page
                const pages = currentData.pages.map((pageData) => {
                    // Some pages might be wrapped in an object with a "data" key
                    if ("data" in pageData) {
                        const { data: timeslotsPageData } = pageData as unknown as TData;
                        if (isArray(timeslotsPageData)) {
                            return {
                                ...pageData,
                                data: timeslotsPageData.filter((item) => item.id !== id),
                            };
                        }
                    }

                    // Otherwise, if it's an array, filter it directly
                    if (isArray(pageData)) {
                        return pageData.filter((item) => item.id !== id);
                    }

                    return pageData;
                });

                queryClient.setQueryData(queryKey, { ...currentData, pages });
            }
        }
    } catch (error: any) {
        console.error(`Error removing item with id ${id} from cache of collection: ${collection}`, error);
        Sentry.captureException(error, {
            tags: { message: `Error removing item with id ${id} from cache of collection: ${collection}` },
        });
    }
};
